import {
    kBackhand,
    kForehand,
    kReturnFirst,
    kReturning,
    kReturnSecond,
    kServeFirst,
    kServeFootfault,
    kServeLet,
    kServeNet,
    kServeOut,
    kServeOutOffNet,
    kServeSecond,
    kServing,
} from "./pstsDefines";
import { getRalliesInMatch } from "./StatsObject";

export const SERVE_FORMATION_LABELS = ["Traditional", "Eye", "Squeeze", "Tandem", "2 Back"];
export const RETURN_FORMATION_LABELS = ["Traditional", "2 Back"];
export const NET_POSITION_LABELS = ["Hold", "Switch", "Cover Middle", "Left", "Right"];
export const SERVE_ZONE_LABELS = ["Wide", "Body", "T"];

// Breakdown modes offered for every category except Net Position (that's a
// whole-team net-play strategy for the point, not tied to one server/serve#).
export const BREAKDOWN_MODES = [
  { id: "combined", label: "Combined" },
  { id: "player", label: "Player" },
  { id: "serve", label: "1st/2nd" },
  { id: "playerServe", label: "Player + 1st/2nd" },
];

// Resolve which team of a doubles match is the "focus" team for playerGuid —
// duplicated (deliberately) from GeneralReport.jsx's isFocusTeamA rather than
// imported, since that lives in a page module and this is a shared util.
function resolveFocusTeam(m, playerGuid) {
  if (!m?.isDoubles) return null;
  if (!playerGuid) return m.teamA;
  const teamAGuid =
    m.teamA?.guid ||
    (m.teamA?.player1?.guid && m.teamA?.player2?.guid
      ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}`
      : null);
  const isA =
    teamAGuid === playerGuid ||
    m.teamA?.player1?.guid === playerGuid ||
    m.teamA?.player2?.guid === playerGuid;
  return isA ? m.teamA : m.teamB;
}

// Serve landing zone (Wide/Body/T), normalised so deuce- and ad-side serves
// into the equivalent physical zone map to the same index — mirrors
// TennisCourt.jsx's serveZone(), which is already proven against real data.
// Returns null for lets (replayed, no outcome) or unparsable positions.
function serveDirectionZone(e) {
  if (e.outcome === kServeLet || !e.endBall) return null;
  const parts = e.endBall.split(",");
  if (parts.length !== 2) return null;
  let x = parseFloat(parts[0]);
  let y = parseFloat(parts[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (y > 100) {
    x = 100 - x;
    y = 200 - y;
  }
  const isFault =
    e.outcome === kServeFootfault ||
    e.outcome === kServeOut ||
    e.outcome === kServeNet ||
    e.outcome === kServeOutOffNet;
  let zone = -1;
  if ((x >= 12.5 && x < 25) || (x >= 75 && x < 87.5)) zone = 0; // Wide
  else if ((x >= 25 && x < 37.5) || (x >= 62.5 && x < 75)) zone = 1; // Body
  else if ((x >= 37.5 && x < 50) || (x >= 50 && x < 62.5)) zone = 2; // T
  if (zone === -1) return null;
  return { zone, isFault };
}

function playerName(p) {
  if (!p) return "Player";
  return `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "Player";
}

// Serve events store 1st/2nd directly in subskill1 (kServeFirst/kServeSecond).
function serveNumberOf(e) {
  if (e.subskill1 === kServeFirst) return "first";
  if (e.subskill1 === kServeSecond) return "second";
  return null;
}

// Return events sometimes reuse the serve constants for subskill1 instead of
// the kReturnFirst/kReturnSecond ones — mirrors TennisCourt.jsx's
// isFirstReturn/isSecondReturn, which check both for the same reason.
function returnServeNumberOf(e) {
  if (e.subskill1 === kReturnFirst || e.subskill1 === kServeFirst) return "first";
  if (e.subskill1 === kReturnSecond || e.subskill1 === kServeSecond) return "second";
  return null;
}

function emptyLeaf() {
  return { points: 0, won: 0, faults: 0 };
}

function addLeaf(leaf, won, isFault) {
  leaf.points++;
  if (isFault) leaf.faults++;
  else if (won) leaf.won++;
}

// A single formation/zone/hand row, pre-aggregated 4 ways so the UI can
// switch breakdown mode instantly without recomputing from raw events.
function emptyRow(index, label) {
  return {
    index,
    label,
    total: emptyLeaf(),
    byPlayer: {}, // guid -> { name, points, won, faults }
    byServe: { first: emptyLeaf(), second: emptyLeaf() },
    byPlayerServe: {}, // guid -> { name, first: leaf, second: leaf }
  };
}

function recordRow(row, { playerGuid, playerName: name, serveNum, won, isFault = false }) {
  if (!row) return;
  addLeaf(row.total, won, isFault);
  if (playerGuid) {
    if (!row.byPlayer[playerGuid]) row.byPlayer[playerGuid] = { name, ...emptyLeaf() };
    addLeaf(row.byPlayer[playerGuid], won, isFault);
  }
  if (serveNum) {
    addLeaf(row.byServe[serveNum], won, isFault);
  }
  if (playerGuid && serveNum) {
    if (!row.byPlayerServe[playerGuid]) {
      row.byPlayerServe[playerGuid] = { name, first: emptyLeaf(), second: emptyLeaf() };
    }
    addLeaf(row.byPlayerServe[playerGuid][serveNum], won, isFault);
  }
}

// Expand a row into the sub-rows implied by `mode` — used by the report UI.
// `combined` returns the row itself as a single entry.
export function expandRow(row, mode) {
  if (!row) return [];
  if (mode === "player") {
    return Object.values(row.byPlayer)
      .sort((a, b) => b.points - a.points)
      .map((p) => ({ label: p.name, points: p.points, won: p.won, faults: p.faults }));
  }
  if (mode === "serve") {
    return [
      { label: "1st Serve", ...row.byServe.first },
      { label: "2nd Serve", ...row.byServe.second },
    ].filter((r) => r.points > 0);
  }
  if (mode === "playerServe") {
    const out = [];
    const players = Object.values(row.byPlayerServe).sort(
      (a, b) => b.first.points + b.second.points - (a.first.points + a.second.points),
    );
    for (const p of players) {
      if (p.first.points > 0) out.push({ label: `${p.name} \u2014 1st`, ...p.first });
      if (p.second.points > 0) out.push({ label: `${p.name} \u2014 2nd`, ...p.second });
    }
    return out;
  }
  return [{ label: row.label, ...row.total }];
}

function emptyBucket() {
  return {
    serveFormations: SERVE_FORMATION_LABELS.map((label, index) => emptyRow(index, label)),
    returnFormations: RETURN_FORMATION_LABELS.map((label, index) => emptyRow(index, label)),
    // Net Position has no breakdown (see BREAKDOWN_MODES comment) — kept as
    // the original flat shape.
    netPositions: NET_POSITION_LABELS.map((label, index) => ({ index, label, points: 0, won: 0 })),
    serveDirection: SERVE_ZONE_LABELS.map((label, index) => emptyRow(index, label)),
    returnHand: {
      forehand: emptyRow(0, "Forehand"),
      backhand: emptyRow(1, "Backhand"),
    },
  };
}

/**
 * Doubles-only match analysis: formation win rates (serve/return/net),
 * serve direction success, and return hand (forehand/backhand) success —
 * each (except net position) pre-aggregated by individual player and by
 * 1st/2nd serve so the report can offer those breakdowns without re-scanning
 * events. Attribution is always to whichever team actually served/returned
 * that point (via getRalliesInMatch's r.servingTeam/r.teamwinner, both direct
 * object references — see StatsObject.js), not to a fixed "team A/B" slot.
 * Returns `{ focus, opposition }` — `opposition` is null unless viewing
 * exactly one match (multi-match opponents aren't meaningfully mergeable).
 */
export function computeDoublesAnalysis(matches, playerGuid) {
  const focus = emptyBucket();
  const opposition = emptyBucket();
  const singleMatch = matches.length === 1;

  for (const m of matches) {
    if (!m?.isDoubles) continue;
    const focusTeam = resolveFocusTeam(m, playerGuid);
    const oppTeam = focusTeam === m.teamA ? m.teamB : m.teamA;
    if (!focusTeam || !oppTeam) continue;

    const rallies = getRalliesInMatch(m);
    for (const r of rallies) {
      if (!r.server || !r.servingTeam) continue;
      const servingIsFocus = r.servingTeam === focusTeam;
      const serverBucket = servingIsFocus ? focus : opposition;
      const receiverBucket = servingIsFocus ? opposition : focus;
      const receivingTeam = servingIsFocus ? oppTeam : focusTeam;
      const servingWon = r.teamwinner === r.servingTeam;
      const receivingWon = r.teamwinner === receivingTeam;

      const serveEvent = r.events.find((e) => e.skill === kServing);
      if (serveEvent) {
        // e.player is the INDIVIDUAL for doubles events (team-level guids
        // are only used for match.teamA/teamB themselves) — see pstsFile.js.
        const serverGuid = serveEvent.player?.guid ?? serveEvent.playerGuid ?? null;
        const serverName = playerName(serveEvent.player);
        const serveNum = serveNumberOf(serveEvent);

        const sf = serveEvent.serveFormation ?? 0;
        recordRow(serverBucket.serveFormations[sf], { playerGuid: serverGuid, playerName: serverName, serveNum, won: servingWon });

        const np = serveEvent.netPosition ?? 0;
        if (serverBucket.netPositions[np]) {
          serverBucket.netPositions[np].points++;
          if (servingWon) serverBucket.netPositions[np].won++;
        }

        const zoneInfo = serveDirectionZone(serveEvent);
        if (zoneInfo) {
          recordRow(serverBucket.serveDirection[zoneInfo.zone], {
            playerGuid: serverGuid,
            playerName: serverName,
            serveNum,
            won: servingWon,
            isFault: zoneInfo.isFault,
          });
        }
      }

      const returnEvent = r.events.find((e) => e.skill === kReturning);
      if (returnEvent) {
        const returnerGuid = returnEvent.player?.guid ?? returnEvent.playerGuid ?? null;
        const returnerName = playerName(returnEvent.player);
        const serveNum = returnServeNumberOf(returnEvent);

        const rf = returnEvent.returnFormation ?? 0;
        recordRow(receiverBucket.returnFormations[rf], { playerGuid: returnerGuid, playerName: returnerName, serveNum, won: receivingWon });

        if (returnEvent.hand === kForehand) {
          recordRow(receiverBucket.returnHand.forehand, { playerGuid: returnerGuid, playerName: returnerName, serveNum, won: receivingWon });
        } else if (returnEvent.hand === kBackhand) {
          recordRow(receiverBucket.returnHand.backhand, { playerGuid: returnerGuid, playerName: returnerName, serveNum, won: receivingWon });
        }
      }
    }
  }

  return { focus, opposition: singleMatch ? opposition : null };
}
